import classNames from 'classnames';
import { default as React, useRef, useState } from 'react';
import AutoSuggest from 'react-autosuggest';

import { getTranslation, keys } from '../../../Resources/Translations';
import { getFor } from '../API';
import { FormInputValidationIssues } from './FormInputValidationIssues';
import { ValidationIssue, ValidationType } from './FormInterfaces';
import { getPostalCodeFirst } from './PostalCodeInputUtil';

export interface PostalCodeInputProps {
    readonly label?: JSX.Element;
    readonly autocompleteUrl: string;
    readonly value?: string;
    readonly onChange: (value: string, errors: ValidationIssue[]) => void;
    readonly hasError: boolean;
    readonly id?: string;
    readonly validationIssues?: ValidationIssue[];
    readonly onBlur: () => void;
    readonly displayName: string;
}

export const PostalCodeInput = (props: PostalCodeInputProps) => {
    const minTriggerInputCharNumber = 2;
    const fetchingTimeout = 300;

    const [value, setValue] = useState(props.value || '');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [fetchedSuggestions, setFetchedSuggestions] = useState<string[]>([]);
    const [showError, setShowError] = useState<boolean>(false);
    const fetchTimeout = useRef<NodeJS.Timeout>();

    React.useEffect(() => {
        setValue(props.value || '');
    }, [props.value]);

    React.useEffect(() => {
        const errors = getErrors(value);
        errors.length >= 1 && props.onChange(value, errors);
    }, [fetchedSuggestions, showError]);

    React.useEffect(() => {
        if (props.value && props.value.length > 1) {
            getFor<string[]>(`${props.autocompleteUrl}?postalCode=${value.substr(0, 2).trim()}`).then((result) => {
                const resultsWithPostalCodeFirst = result.map((x) => getPostalCodeFirst(x));
                setFetchedSuggestions(resultsWithPostalCodeFirst);
                setSuggestions(resultsWithPostalCodeFirst.filter((x) => x.toLowerCase().includes(value.toLowerCase())));
                setShowError(true);
            });
        }
    }, []);

    const getErrors = (value: string) => {
        if (!value) {
            return [{ type: ValidationType.Error, message: `${props.displayName} ${getTranslation(keys['form.errors.required'])}` }];
        }

        return fetchedSuggestions.map((x) => x.toLowerCase()).indexOf(value.toLowerCase()) < 0 && value && showError
            ? [
                  {
                      type: ValidationType.Error,
                      message: getTranslation(keys['checkout.incorrectPostalCodeOrCity'])
                  }
              ]
            : [];
    };

    const onSelectSuggestion = (e: React.FormEvent, suggestionValue: string) => {
        e.preventDefault();
        e.stopPropagation();
        selectSuggestion(suggestionValue);
    };

    const selectSuggestion = (suggestionValue: string) => {
        setValue(suggestionValue);
        const errors = getErrors(suggestionValue);
        props.onChange(suggestionValue, errors);
    };

    const handleValueChange = (value: string) => {
        setValue(value);
        const errors = getErrors(value);
        props.onChange(value, errors);
    };

    const handleBlur = () => {
        props.onBlur();
        let newValue = value;
        if (suggestions.length === 1 && newValue) {
            newValue = suggestions[0];
            selectSuggestion(newValue);
        }
        handleValueChange(newValue);
    };

    // Suggestions which code or city starts with user input come first
    const sortSuggestions = (suggestions: string[], userInput: string): string[] => {
        const lowercaseUserInput = userInput.toLowerCase();

        return suggestions.sort((a, b) => {
            const [postalA, cityA] = a.split(' ');
            const [postalB, cityB] = b.split(' ');

            const aMatches = postalA.startsWith(lowercaseUserInput) || cityA.toLowerCase().startsWith(lowercaseUserInput);
            const bMatches = postalB.startsWith(lowercaseUserInput) || cityB.toLowerCase().startsWith(lowercaseUserInput);

            if (aMatches === bMatches) {
                return 0;
            }

            return aMatches ? -1 : 1;
        });
    };

    const fetchSuggestions = (value: string) => {
        if (value.length >= minTriggerInputCharNumber) {
            if (fetchTimeout.current !== undefined) {
                clearTimeout(fetchTimeout.current);
            }

            fetchTimeout.current = setTimeout(() => {
                const postalCodeQueryValue = value.trim().split(' ')[0];

                getFor<string[]>(`${props.autocompleteUrl}?postalCode=${postalCodeQueryValue}`).then((result) => {
                    const resultsWithPostalCodeFirst = result.map((x) => getPostalCodeFirst(x));
                    const sortedSuggestions = sortSuggestions(resultsWithPostalCodeFirst, postalCodeQueryValue);
                    setSuggestions(sortedSuggestions);
                    setFetchedSuggestions(sortedSuggestions);
                    setShowError(true);
                });
            }, fetchingTimeout);
        }
    };

    return (
        <div className={classNames('NewForm__FieldWrapper', props.hasError && 'NewForm__Error')}>
            <div className="NewForm__Field">
                <label htmlFor={props.id}>{props.label}</label>
                <AutoSuggest
                    suggestions={suggestions}
                    id={props.id}
                    onSuggestionsFetchRequested={({ value }) => fetchSuggestions(value)}
                    onSuggestionsClearRequested={() => setSuggestions([])}
                    onSuggestionSelected={(e, { suggestionValue }) => onSelectSuggestion(e, suggestionValue)}
                    getSuggestionValue={(suggestion) => suggestion}
                    renderSuggestion={(suggestion) => <span className={'PostalCodeInput__Suggestion'}>{suggestion}</span>}
                    inputProps={{
                        value: value,
                        onBlur: handleBlur,
                        onChange: (_, { newValue }) => {
                            handleValueChange(newValue);
                        }
                    }}
                    highlightFirstSuggestion
                />
            </div>
            <FormInputValidationIssues validationIssue={props.validationIssues} />
        </div>
    );
};
